import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchPublicProfiles, chunkIds } from './publicProfilesBatch';
import { sortPedalsByListedAt } from './sortPedalsByListedAt';
import { throwIfOfflineEmpty } from './recoverableLoad';
import { offerModeOf } from './offerMode';

/**
 * One read of the rows behind "who wants my pedals": the server-maintained
 * wantsMyPedals docs for this owner, as plain data objects.
 *
 * The dashboard used to run this identical query three times per mount
 * (recommendations, the potential-match banner set, and the Match
 * Opportunities counter). Fetch it once and hand the rows to each consumer —
 * every consumer keeps its own filtering/count logic and accepts the rows as
 * an OPTIONAL input, falling back to running this query itself when absent,
 * so non-dashboard callers are unchanged.
 */
export async function fetchWantsMyPedalsRows(userId) {
  const snapshot = await getDocs(
    query(
      collection(db, 'wantsMyPedals'),
      where('pedalOwnerId', '==', userId)
    )
  );
  // Offline, this query RESOLVES cache-empty instead of rejecting, which
  // used to make every consumer render a legitimate-looking empty section
  // (Phase 2c, 2026-09-29). Throw the unconfirmed result; a
  // server-confirmed zero-rows result still returns [] normally.
  throwIfOfflineEmpty(snapshot, 'wantsMyPedalsRows');
  return snapshot.docs.map(d => d.data());
}

/**
 * Get pedals from users whose wants match the current user's pedals.
 * Uses the server-side wantsMyPedals collection instead of scanning the
 * entire wantList on the client.
 *
 * @param {string} currentUserId - The current user's ID
 * @param {number} limit - Max number of pedals to return (null for all)
 * @param {Array|null} [options.wmpRows] - Pre-fetched wantsMyPedals row data
 *   for this owner (from fetchWantsMyPedalsRows). Absent/null: fetched here,
 *   exactly as before.
 * @returns {Promise<Array>} Array of pedals with match information
 */
export async function getPedalsFromUsersWhoWantMine(currentUserId, limit = null, { wmpRows = null } = {}) {
  try {
    // 1. Pre-computed one-directional matches (indexed on pedalOwnerId) —
    // shared rows when the caller already fetched them, own query otherwise.
    const wmpDocs = wmpRows || await fetchWantsMyPedalsRows(currentUserId);

    if (wmpDocs.length === 0) {
      return [];
    }

    // 2. Group by wantingUserId and collect which of my pedals each user wants
    const myPedalIds = new Set();
    const interestedUsersMap = new Map(); // wantingUserId -> Set<pedalId>

    for (const wmp of wmpDocs) {
      myPedalIds.add(wmp.pedalId);
      if (!interestedUsersMap.has(wmp.wantingUserId)) {
        interestedUsersMap.set(wmp.wantingUserId, new Set());
      }
      interestedUsersMap.get(wmp.wantingUserId).add(wmp.pedalId);
    }


    // 3. Fetch my pedal details to reconstruct matchInfo.wantedPedals shape.
    // Batched documentId()-in reads (one round trip per 30 pedals) instead of
    // one getDoc per pedal; an in-query returns only existing docs, matching
    // the old exists() check, and a failed chunk contributes nothing, matching
    // the old per-doc catch.
    // Falsy ids are dropped BEFORE chunking: an `in` array containing
    // undefined throws client-side and would poison the whole chunk (up to
    // 29 valid entries). The old per-id/per-user code lost only the bad row
    // itself, so filtering here preserves that exact degradation. No such
    // rows exist in prod today (audited 7,553 rows, Sep 17 2026) — this
    // guards future malformed writes.
    const myPedalMap = new Map();
    await Promise.all(
      chunkIds(Array.from(myPedalIds).filter(Boolean)).map(async (slice) => {
        try {
          const snap = await getDocs(
            query(collection(db, 'listings'), where(documentId(), 'in', slice))
          );
          snap.docs.forEach(pedalDoc => {
            const data = pedalDoc.data();
            if (offerModeOf(data) === 'sell') return; // my sale-only pedal: purchase lead, not a trade (plan §5)
            myPedalMap.set(pedalDoc.id, {
              id: pedalDoc.id, make: data.make, model: data.model, effectTypes: data.effectTypes
            });
          });
        } catch (err) {
          console.error('Error batch-fetching my pedal details:', err);
        }
      })
    );

    // 4. Fetch the interested users' active listings, batched: one
    // ownerId-in query per 30 users instead of one query per user. The
    // in-query returns exactly the union of the per-user queries it
    // replaced; a failed chunk degrades to those users contributing
    // nothing, same as the old per-user catch.
    const docsByOwner = new Map(); // ownerId -> QueryDocumentSnapshot[]
    const interestedUserIds = Array.from(interestedUsersMap.keys()).filter(Boolean);
    await Promise.all(
      chunkIds(interestedUserIds).map(async (slice) => {
        try {
          const snap = await getDocs(
            query(
              collection(db, 'listings'),
              where('ownerId', 'in', slice),
              where('active', '==', true)
            )
          );
          snap.docs.forEach(d => {
            const ownerId = d.data().ownerId;
            if (!docsByOwner.has(ownerId)) docsByOwner.set(ownerId, []);
            docsByOwner.get(ownerId).push(d);
          });
        } catch (err) {
          console.error(`Error batch-fetching listings for interested users [${slice.join(', ')}]:`, err);
        }
      })
    );

    // Assemble in the SAME insertion order as the per-user version this
    // replaced (interested users in wmp-row order; within a user, documentId
    // order — which is what both the old per-owner query and the in-query
    // return). sortPedalsByListedAt has no tie-breaker and JS sort is stable,
    // so preserving insertion order keeps equal-listedAt ties rendering
    // identically to the old code.
    const recommendations = [];
    for (const [userId, wantedPedalIds] of interestedUsersMap.entries()) {
      const wantedPedals = Array.from(wantedPedalIds)
        .map(pid => myPedalMap.get(pid))
        .filter(Boolean);
      if (wantedPedals.length === 0) continue; // they only want my sale-only pedals

      for (const d of (docsByOwner.get(userId) || [])) {
        const pedalData = d.data();
        if (pedalData.inTrade || pedalData.hiddenFromPedalboard) continue;
        if (offerModeOf(pedalData) === 'sell') continue; // their sale-only pedal cannot be traded for
        recommendations.push({
          id: d.id,
          ...pedalData,
          matchInfo: {
            wantedPedals,
            interestedUserId: userId
          }
        });
      }
    }


    // Sort by newest listing first across the full set, then apply the limit
    // so the carousel always shows the most recently listed pedals.
    const sorted = sortPedalsByListedAt(recommendations);
    const result = limit ? sorted.slice(0, limit) : sorted;
    return result;

  } catch (error) {
    // Unconfirmed offline result: surface it so the dashboard can show its
    // couldn't-load state instead of a false "No recommendations yet".
    // Every caller of this helper already catches and degrades.
    if (error?.name === 'OfflineResultError') throw error;
    console.error('❌ Error getting recommendations:', error);
    return [];
  }
}

/**
 * Enrich pedals with owner data (username, badge)
 * ⚡ PERFORMANCE OPTIMIZED: Batch fetches all unique owners in parallel
 * @param {Array} pedals - Array of pedal objects
 * @returns {Promise<Array>} Pedals with owner data added
 */
export async function enrichPedalsWithOwnerData(pedals) {
  if (!pedals || pedals.length === 0) {
    return [];
  }

  // Step 1: Extract unique owner IDs
  const uniqueOwnerIds = [...new Set(pedals.map(p => p.ownerId))];

  // Step 2: Batch fetch all owner data in parallel
  const ownerDataMap = {};
  const defaultOwnerData = {
    username: 'Unknown',
    completedTrades: 0,
    photoURL: '',
    membershipTier: 'free',
    averageRating: 0,
    lastActiveWeek: null,
    responderBadge: null
  };

  // Batched documentId()-in reads (one round trip per 30 owners) instead of
  // one getDoc per owner. {} for a missing profile normalizes to exactly
  // defaultOwnerData below, matching the per-owner version this replaced.
  const rawProfiles = await fetchPublicProfiles(uniqueOwnerIds, { fallback: {} });
  for (const ownerId of uniqueOwnerIds) {
    const userData = rawProfiles[ownerId] || {};
    ownerDataMap[ownerId] = {
      username: userData.username || 'Unknown',
      completedTrades: userData.completedTrades || 0,
      photoURL: userData.photoURL || '',
      membershipTier: userData.membershipTier || 'free',
      averageRating: userData.averageRating || 0,
      // lastActiveAt is no longer mirrored — the badge utility prefers lastActiveWeek.
      lastActiveWeek: userData.lastActiveWeek || null,
      responderBadge: userData.responderBadge || null
    };
  }

  // Step 3: Enrich pedals with fetched owner data
  const enrichedPedals = pedals.map(pedal => {
    const ownerData = ownerDataMap[pedal.ownerId] || defaultOwnerData;
    return {
      ...pedal,
      owner: {
        username: ownerData.username,
        badge: ownerData.completedTrades || 0,
        membershipTier: ownerData.membershipTier,
        averageRating: ownerData.averageRating,
        lastActiveWeek: ownerData.lastActiveWeek,
        lastActiveAt: ownerData.lastActiveAt,
        responderBadge: ownerData.responderBadge || null
      },
      ownerPhotoURL: ownerData.photoURL
    };
  });

  return sortPedalsByListedAt(enrichedPedals);
}

